import { useQuery, useLazyQuery } from "@apollo/client/react"
import { DateTime } from "luxon"
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react"

import { PlanViewer, type VenuePlanData } from "@/components/plan"
import HorizontalScheduleWithActions from "@/components/schedule/horizontal_schedule_with_actions"
import { STATUS_CANCELLED, STATUS_NO_SHOW, STATUS_REMOVED } from "@/constants/reservations"
import { GetReservationsDocument, GetOpeningHoursDocument, ReservationDateField } from "@/graphql/graphql"
import { createHoursList } from "@/shared/datetime"
import { getOpeningHoursForDate } from "@/utils/opening_hours"

import { type VenueShape } from "../reservation_form_types"

interface ExistingTableSlot {
  tableId: string
  startTime: string
  endTime: string
}

const EMPTY_TABLE_SLOTS: ExistingTableSlot[] = []

interface ReservationPlanSelectorProps {
  venue: { slug: VenueShape["slug"]; tables: Array<{ id: string; name: string }> }
  venuePlans: VenuePlanData[]
  selectedTableIds: string[]
  date: DateTime | string | null
  startTime: DateTime | string | null
  endTime: DateTime | string | null
  onPendingChangesChange?: (hasPendingChanges: boolean) => void
  currentReservationId?: string
  fullScreen?: boolean
  multiSelect?: boolean
  reservationBuffer?: number
  mode?: "edit" | "add"
  onTableSelect?: (tableIds: string[]) => void
  onTimeSelect?: (startTime: DateTime, endTime: DateTime) => void
  onAddTableSlot?: (tableId: string, startTime: DateTime, endTime: DateTime) => void
  existingTableSlots?: ExistingTableSlot[]
}

const ReservationPlanSelector: React.FC<ReservationPlanSelectorProps> = ({
  venue,
  venuePlans,
  selectedTableIds,
  date,
  startTime,
  endTime,
  onPendingChangesChange,
  currentReservationId,
  fullScreen,
  multiSelect = false,
  reservationBuffer = 0,
  mode = "edit",
  onTableSelect,
  onTimeSelect,
  onAddTableSlot,
  existingTableSlots = EMPTY_TABLE_SLOTS,
}) => {
  const [highlightedTableIds, setHighlightedTableIds] = useState<string[]>([])
  const [internalStartTime, setInternalStartTime] = useState<DateTime | null>(null)
  const [internalEndTime, setInternalEndTime] = useState<DateTime | null>(null)
  const [scrollToStartTime, setScrollToStartTime] = useState<DateTime | null>(null)
  const [scrollToEndTime, setScrollToEndTime] = useState<DateTime | null>(null)
  const initializedRef = useRef(false)

  useEffect(() => {
    if (mode === "edit") {
      setHighlightedTableIds(selectedTableIds)
    } else if (mode === "add" && !initializedRef.current && selectedTableIds.length > 0) {
      initializedRef.current = true
      setHighlightedTableIds(selectedTableIds)

      const firstSlot = existingTableSlots.find((slot) => slot.tableId === selectedTableIds[0])
      if (firstSlot) {
        const startDateTime = DateTime.fromISO(firstSlot.startTime)
        const endDateTime = DateTime.fromISO(firstSlot.endTime)
        setInternalStartTime(startDateTime)
        setInternalEndTime(endDateTime)
        setScrollToStartTime(startDateTime)
        setScrollToEndTime(endDateTime)
      }
    }
  }, [selectedTableIds, mode, existingTableSlots])

  const [fetchReservations, { data: reservationsData }] = useLazyQuery(GetReservationsDocument)

  const { data: openingHoursData } = useQuery(GetOpeningHoursDocument, {
    variables: { slug: venue.slug },
  })

  const normalizeDate = (d: DateTime | string | null): DateTime | null => {
    if (!d) return null
    if (typeof d === "string") return DateTime.fromISO(d)
    return d
  }

  const normalizedDate = normalizeDate(date)

  const { startHour, endHour } = useMemo(
    () => getOpeningHoursForDate(openingHoursData?.venue?.openingHours, normalizedDate),
    [openingHoursData?.venue?.openingHours, normalizedDate],
  )

  const timeOptions = useMemo(
    () => createHoursList(normalizedDate, startHour, endHour),
    [normalizedDate, startHour, endHour],
  )

  const mapTables = venue.tables.map((t) => ({ id: t.id, name: t.name }))

  const handleTableClick = useCallback(
    (tableId: string, event?: { ctrlKey?: boolean; metaKey?: boolean }) => {
      if (mode === "add") {
        setHighlightedTableIds([tableId])

        const existingSlot = existingTableSlots.find((slot) => slot.tableId === tableId)
        if (existingSlot) {
          const startDateTime = DateTime.fromISO(existingSlot.startTime)
          const endDateTime = DateTime.fromISO(existingSlot.endTime)
          setInternalStartTime(startDateTime)
          setInternalEndTime(endDateTime)
          setScrollToStartTime(startDateTime)
          setScrollToEndTime(endDateTime)
        } else {
          setInternalStartTime(null)
          setInternalEndTime(null)
          setScrollToStartTime(null)
          setScrollToEndTime(null)
        }
      } else if (multiSelect && (event?.ctrlKey || event?.metaKey)) {
        setHighlightedTableIds((prev) => {
          if (prev.includes(tableId)) {
            return prev.filter((id) => id !== tableId)
          }
          return [...prev, tableId]
        })
      } else {
        setHighlightedTableIds([tableId])
      }
    },
    [multiSelect, mode, existingTableSlots],
  )

  const handleClickAway = useCallback(() => {
    if (mode === "add") {
      setHighlightedTableIds([])
    } else {
      setHighlightedTableIds([])
    }
    setInternalStartTime(null)
    setInternalEndTime(null)
  }, [mode])

  useEffect(() => {
    if (!normalizedDate) return

    const from = normalizedDate.startOf("day").toISODate()
    const to = normalizedDate.endOf("day").toISODate()

    void fetchReservations({
      variables: {
        slug: venue.slug,
        dateFilter: { field: ReservationDateField.ReservationDate, from, to },
      },
    })
  }, [normalizedDate, venue.slug, fetchReservations])

  const allDayReservations = useMemo(
    () =>
      reservationsData?.venue?.reservations?.nodes
        ?.filter(
          (r) =>
            (r.status as string) !== STATUS_CANCELLED &&
            (r.status as string) !== STATUS_NO_SHOW &&
            (r.status as string) !== STATUS_REMOVED,
        )
        .map((r) => ({
          tableId: r.table.id,
          id: r.id,
          startTime: r.startTime,
          endTime: r.endTime,
        })) || [],
    [reservationsData],
  )

  const dayReservations = useMemo(() => {
    if (highlightedTableIds.length === 0) return []
    const firstHighlightedTableId = highlightedTableIds[0]
    return allDayReservations
      .filter((r) => r.tableId === firstHighlightedTableId)
      .map((r) => ({ id: r.id, startTime: r.startTime, endTime: r.endTime }))
  }, [allDayReservations, highlightedTableIds])

  const handleDrag = useCallback((start: DateTime, end: DateTime) => {
    setInternalStartTime(start)
    setInternalEndTime(end)
  }, [])

  const handleSave = useCallback(() => {
    if (highlightedTableIds.length === 0) return

    const finalStartTime = internalStartTime
    const finalEndTime = internalEndTime

    if (mode === "add" && onAddTableSlot) {
      if (finalStartTime && finalEndTime) {
        onAddTableSlot(highlightedTableIds[0], finalStartTime, finalEndTime)
      }
      setHighlightedTableIds([])
    } else if (mode === "edit" && onTableSelect && onTimeSelect) {
      onTableSelect(highlightedTableIds)
      if (finalStartTime && finalEndTime) {
        onTimeSelect(finalStartTime, finalEndTime)
      }
    }

    setInternalStartTime(null)
    setInternalEndTime(null)
  }, [highlightedTableIds, internalStartTime, internalEndTime, mode, onAddTableSlot, onTableSelect, onTimeSelect])

  const handleCancel = useCallback(() => {
    if (mode === "edit") {
      setHighlightedTableIds(selectedTableIds)
    } else {
      setHighlightedTableIds([])
    }
    setInternalStartTime(null)
    setInternalEndTime(null)
  }, [selectedTableIds, mode])

  const freeTableIds = useMemo(() => {
    if (!internalStartTime || !internalEndTime) return []

    const allTableIds = venue.tables.map((t) => t.id)
    return allTableIds.filter((tableId: string) => {
      const tableReservations = allDayReservations.filter((r) => r.tableId === tableId && r.id !== currentReservationId)
      return !tableReservations.some((r) => {
        const resStart = DateTime.fromISO(r.startTime)
        const resEnd = DateTime.fromISO(r.endTime)
        return internalStartTime < resEnd && internalEndTime > resStart
      })
    })
  }, [internalStartTime, internalEndTime, allDayReservations, venue.tables, currentReservationId])

  const hasParentTableAndTimes = selectedTableIds.length > 0 && !!startTime && !!endTime
  const firstSelectedTableId = selectedTableIds[0] || null
  const firstHighlightedTableId = highlightedTableIds[0] || null
  const showParentTimes =
    mode === "edit" && (!hasParentTableAndTimes || firstHighlightedTableId === firstSelectedTableId)

  const timesActuallyChanged = useMemo(() => {
    if (!internalStartTime || !internalEndTime) return false

    if (mode === "add" && firstHighlightedTableId) {
      const existingSlot = existingTableSlots.find((slot) => slot.tableId === firstHighlightedTableId)
      if (existingSlot) {
        const existingStart = DateTime.fromISO(existingSlot.startTime)
        const existingEnd = DateTime.fromISO(existingSlot.endTime)
        return (
          internalStartTime.toMillis() !== existingStart.toMillis() ||
          internalEndTime.toMillis() !== existingEnd.toMillis()
        )
      }
    }

    return true
  }, [internalStartTime, internalEndTime, mode, firstHighlightedTableId, existingTableSlots])

  const tablesChanged =
    highlightedTableIds.length > 0 &&
    (highlightedTableIds.length !== selectedTableIds.length ||
      highlightedTableIds.some((id) => !selectedTableIds.includes(id)))

  let hasPendingChanges = false
  if (mode === "add") {
    hasPendingChanges = highlightedTableIds.length > 0 && timesActuallyChanged
  } else if (hasParentTableAndTimes) {
    const sameTablesWithNewTimes = !tablesChanged && timesActuallyChanged
    const differentTablesWithTimes = tablesChanged && timesActuallyChanged
    hasPendingChanges = sameTablesWithNewTimes || differentTablesWithTimes
  } else {
    hasPendingChanges = highlightedTableIds.length > 0 && (tablesChanged || timesActuallyChanged)
  }

  useEffect(() => {
    onPendingChangesChange?.(hasPendingChanges)
  }, [hasPendingChanges, onPendingChangesChange])

  return (
    <PlanViewer
      data-testid="panel-reservations-plan-selector"
      key={fullScreen ? "fullscreen" : "normal"}
      venuePlans={venuePlans}
      tables={mapTables}
      selectedTableIds={selectedTableIds}
      highlightedTableIds={highlightedTableIds}
      freeTableIds={freeTableIds}
      onTableSelect={handleTableClick}
      onClickAway={handleClickAway}
      showSchedule={true}
      scheduleComponent={
        <HorizontalScheduleWithActions
          timeOptions={timeOptions}
          busyBlocks={dayReservations
            .filter((r) => r.id !== currentReservationId)
            .map((r) => ({ startTime: r.startTime, endTime: r.endTime }))}
          internalStartTime={internalStartTime}
          internalEndTime={internalEndTime}
          parentStartTime={startTime}
          parentEndTime={endTime}
          onTimeChange={handleDrag}
          onSave={handleSave}
          onCancel={handleCancel}
          hasDate={!!normalizedDate}
          hasHighlightedTable={highlightedTableIds.length > 0}
          showParentTimes={showParentTimes}
          hasPendingChanges={hasPendingChanges}
          reservationBuffer={reservationBuffer}
          mode={mode}
          scrollToStartTime={scrollToStartTime}
          scrollToEndTime={scrollToEndTime}
        />
      }
    />
  )
}

export default ReservationPlanSelector
